import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { Bot, Loader2, MessageCircle, Trash2, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useConversation,
  useConversationMessages,
  useDeleteConversation,
  useHandbackConversation,
  useMarkAsRead,
  useSendImage,
  useSendMessage,
  useSendTemplate,
  useTakeoverConversation,
} from '@/hooks/useMessaging';

import { ChannelBadge } from './ChannelBadge';
import { MessageBubble } from './MessageBubble';
import { MessageInput } from './MessageInput';

import type { MessagingMessage } from '@/lib/api/messaging.api';

interface MessageThreadProps {
  conversationId: string | undefined;
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

const groupMessagesByDate = (messages: MessagingMessage[]) => {
  const groups: Array<{ date: string; messages: MessagingMessage[] }> = [];
  let currentDate = '';

  for (const msg of messages) {
    const msgDate = formatDate(msg.created_at);
    if (msgDate !== currentDate) {
      currentDate = msgDate;
      groups.push({ date: msgDate, messages: [] });
    }
    groups[groups.length - 1].messages.push(msg);
  }

  return groups;
};

export const MessageThread = ({ conversationId }: MessageThreadProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef(0);
  const isInitialLoadRef = useRef(true);
  const navigate = useNavigate();

  const { data: conversation } = useConversation(conversationId);
  const {
    data: messagesPages,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useConversationMessages(conversationId);
  const sendMessage = useSendMessage();
  const sendTemplate = useSendTemplate();
  const sendImage = useSendImage();
  const markAsRead = useMarkAsRead();
  const deleteConversation = useDeleteConversation();
  const takeoverConversation = useTakeoverConversation();
  const handbackConversation = useHandbackConversation();

  const allMessages = messagesPages?.pages.flatMap((page) => page.data) ?? [];
  const sortedMessages = [...allMessages].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
  const groups = groupMessagesByDate(sortedMessages);

  useEffect(() => {
    if (conversationId && conversation && conversation.unread_count > 0) {
      markAsRead.mutate(conversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, conversation?.unread_count]);

  useLayoutEffect(() => {
    if (!scrollContainerRef.current) return;

    if (isInitialLoadRef.current && sortedMessages.length > 0) {
      messagesEndRef.current?.scrollIntoView();
      isInitialLoadRef.current = false;
      return;
    }

    if (isFetchingNextPage) {
      prevScrollHeightRef.current = scrollContainerRef.current.scrollHeight;
    }
  }, [sortedMessages.length, isFetchingNextPage]);

  useEffect(() => {
    if (!isFetchingNextPage && prevScrollHeightRef.current > 0 && scrollContainerRef.current) {
      const newScrollHeight = scrollContainerRef.current.scrollHeight;
      scrollContainerRef.current.scrollTop = newScrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = 0;
    }
  }, [isFetchingNextPage]);

  useEffect(() => {
    if (isInitialLoadRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [sortedMessages.length]);

  useEffect(() => {
    isInitialLoadRef.current = true;
  }, [conversationId]);

  const handleScrollToTop = useCallback(() => {
    if (!scrollContainerRef.current || !hasNextPage || isFetchingNextPage) return;
    if (scrollContainerRef.current.scrollTop < 100) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScrollToTop);
    return () => el.removeEventListener('scroll', handleScrollToTop);
  }, [handleScrollToTop]);

  if (!conversationId) {
    return (
      <div className="text-muted-foreground flex h-full flex-col items-center justify-center gap-3">
        <MessageCircle className="h-12 w-12 opacity-20" />
        <p className="text-sm">Select a conversation to start messaging</p>
      </div>
    );
  }

  const displayName =
    conversation?.contact?.name ||
    (conversation?.channel === 'EMAIL'
      ? conversation?.remote_address
      : conversation?.contact?.phone || conversation?.remote_address) ||
    '';
  const isAiPaused = (conversation?.metadata as Record<string, unknown> | null)?.ai_paused === true;

  const handleSend = (content: string) => {
    sendMessage.mutate({ conversationId, content });
  };

  const handleSendTemplate = (
    templateName: string,
    language: string,
    components?: Array<Record<string, unknown>>,
    renderedText?: string,
  ) => {
    if (!conversationId) return;
    sendTemplate.mutate({ conversationId, templateName, language, components, renderedText });
  };

  const handleSendImage = (file: File) => {
    if (!conversationId) return;
    sendImage.mutate({ conversationId, file });
  };

  const handleDelete = () => {
    if (!conversationId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this conversation and all its messages? This cannot be undone.'))
      return;
    deleteConversation.mutate(conversationId, {
      onSuccess: () => navigate('/messaging'),
    });
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 flex h-9 w-9 items-center justify-center rounded-full">
            <span className="text-primary text-sm font-semibold">
              {(displayName[0] ?? '?').toUpperCase()}
            </span>
          </div>
          <div>
            <h3 className="text-sm font-semibold">{displayName}</h3>
            <p className="text-muted-foreground text-xs">
              {conversation?.remote_address}
              {conversation?.configuration && ` · ${conversation.configuration.name}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {conversation && <ChannelBadge channel={conversation.channel} />}
          {conversation && (
            <Button
              variant={isAiPaused ? 'default' : 'outline'}
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() =>
                isAiPaused
                  ? handbackConversation.mutate(conversationId)
                  : takeoverConversation.mutate(conversationId)
              }
              disabled={takeoverConversation.isPending || handbackConversation.isPending}
            >
              {isAiPaused ? (
                <>
                  <Bot className="h-3.5 w-3.5" />
                  Return to AI
                </>
              ) : (
                <>
                  <UserCheck className="h-3.5 w-3.5" />
                  Take Over
                </>
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-destructive h-8 w-8"
            onClick={handleDelete}
            disabled={deleteConversation.isPending}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 py-3">
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={`flex ${i % 2 ? 'justify-end' : 'justify-start'}`}>
                <Skeleton className="h-12 w-48 rounded-2xl" />
              </div>
            ))}
          </div>
        ) : groups.length === 0 ? (
          <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
            No messages yet
          </div>
        ) : (
          <div className="space-y-4">
            {(hasNextPage || isFetchingNextPage) && (
              <div ref={loadMoreRef} className="flex justify-center py-2">
                {isFetchingNextPage ? (
                  <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
                ) : (
                  <button
                    onClick={() => fetchNextPage()}
                    className="text-muted-foreground hover:text-foreground text-xs"
                  >
                    Load older messages
                  </button>
                )}
              </div>
            )}
            {groups.map((group) => (
              <div key={group.date}>
                <div className="mb-3 flex justify-center">
                  <span className="bg-muted rounded-full px-3 py-1 text-[11px] font-medium">
                    {group.date}
                  </span>
                </div>
                <div className="space-y-2">
                  {group.messages.map((msg) => (
                    <MessageBubble key={msg.id} message={msg} />
                  ))}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <MessageInput
        onSend={handleSend}
        onSendTemplate={conversation?.channel === 'WHATSAPP' ? handleSendTemplate : undefined}
        onSendImage={conversation?.channel === 'WHATSAPP' ? handleSendImage : undefined}
        disabled={sendMessage.isPending || sendTemplate.isPending || sendImage.isPending}
        channel={conversation?.channel}
      />
    </div>
  );
};
